import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Plus, Edit3, Trash2, RefreshCw, Compass, AlertTriangle, Store } from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { BackofficeTrailDto, MerchantDiscoveryOptionsDto } from '@kardume/types';
import { TRAIL_ERROR_MESSAGES, TRAIL_ICON_OPTIONS } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  TagsInput,
  Checkbox,
  Switch,
  Modal,
  Spinner,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';
import {
  TrailFormState,
  buildTrailFilter,
  describeTrailAudience,
  describeTrailVisibility,
  emptyTrailForm,
  filterSignature,
  isFilterEmpty,
  previewSlug,
  toTrailForm,
  toTrailRequest,
  validateTrail,
} from './trails/trailForm';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }

  span {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const CriteriaCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  max-width: 320px;
`;

const StatusCell = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;

  small {
    font-size: 0.72rem;
    line-height: 1.35;
    color: ${(p) => p.theme.textMuted};
    max-width: 220px;
    text-align: right;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const Section = styled.section`
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid ${(p) => p.theme.border};
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.15rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.textPrimary};
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 0.4rem 0.75rem;
  margin-top: 0.6rem;
`;

const InlineToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textSecondary};
`;

/**
 * A prévia da contagem.
 *
 * Fica dentro da moldura do filtro, e não ao lado do botão Salvar, porque o
 * número é consequência do filtro: lido junto dele, ele explica a seleção; lido
 * junto do botão, viraria mais um aviso a ignorar na hora de salvar.
 */
const AudiencePanel = styled.div<{ $warn: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 0.9rem;
  padding: 0.7rem 0.85rem;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => (p.$warn ? p.theme.warning : p.theme.border)};
  background: ${(p) => (p.$warn ? `${p.theme.warning}14` : p.theme.surfaceAlt)};
  color: ${(p) => (p.$warn ? p.theme.warning : p.theme.textSecondary)};
  font-size: 0.82rem;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const ACTIVE_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'true', label: 'Ativas' },
  { value: 'false', label: 'Desativadas' },
];

const ICON_OPTIONS = [
  { value: '', label: 'Sem ícone (bússola)' },
  ...TRAIL_ICON_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** Rótulo curto de cada critério, para a coluna "Critérios" da tabela. */
const criteriaChips = (
  trail: BackofficeTrailDto,
  amenityLabels: Record<string, string>,
  priceSymbols: Record<number, string>
): string[] => {
  const chips: string[] = [];
  (trail.filter?.amenities ?? []).forEach((code) => chips.push(amenityLabels[code] ?? code));
  const ranges = trail.filter?.priceRanges ?? [];
  if (ranges.length > 0) {
    chips.push(ranges.map((value) => priceSymbols[value] ?? String(value)).join(' ou '));
  }
  if (trail.filter?.minRating != null) chips.push(`nota ${trail.filter.minRating}+`);
  if (trail.filter?.minCashback != null) chips.push(`cashback ${trail.filter.minCashback}%+`);
  if (trail.filter?.openNow === true) chips.push('aberto agora');
  return chips;
};

/**
 * Curadoria das trilhas do `/explorar` (card #222).
 *
 * Fica ao lado da Gestão de Feeds/Matérias, e é desenhada no molde daquela
 * tela, porque é a mesma natureza de trabalho editorial e a autorização é
 * literalmente a mesma (`ROLE_BACKOFFICE_FEED_*`) - quem escreve matéria cura
 * trilha. Nenhum papel novo entrou com este card.
 *
 * Duas coisas separam esta tela de um CRUD comum, e as duas são contrato:
 *
 * 1. **A contagem antes de salvar** (`POST .../preview`). Trilha com menos de
 *    duas lojas não é publicada nem indexada, o piso não viaja no JSON e o
 *    servidor não recusa o salvamento - sem o número aqui, alguém cria uma
 *    trilha que nunca aparece e abre chamado dizendo que o backoffice quebrou.
 * 2. **Vocabulário escolhido, nunca digitado.** Comodidades e faixas de preço
 *    saem de `GET /v1/merchants/filters`, a mesma fonte que o core valida.
 *    Campo de texto livre aqui produz trilha morta.
 */
const TrailsPage: React.FC = () => {
  const { showNotification } = useNotification();
  // Regra 2 do CLAUDE.md: o vermelho de excluir sai do tema, e não de um hex
  // literal. É o mesmo `#DC2626` que as telas vizinhas escrevem à mão - nenhuma
  // cor nova entra aqui, e a paleta do #255 continua intocada.
  const theme = useTheme();
  const [search, setSearch] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TrailFormState>(emptyTrailForm());
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<BackofficeTrailDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listTrails({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        active: activeFilter ? activeFilter === 'true' : undefined,
        localityId: cityFilter || undefined,
        sort: 'sortOrder,asc',
      }),
    [page, pageSize, debouncedSearch, activeFilter, cityFilter]
  );

  const { data: localityPage } = useAsync(
    () => backofficeApi.listLocalities({ page: 0, size: 100, active: true, sort: 'name,asc' }),
    []
  );

  /**
   * O vocabulário do filtro, lido uma vez.
   *
   * `GET /v1/merchants/filters` e não o catálogo do backoffice: são a mesma
   * lista, mas a rota do backoffice exige `ROLE_BACKOFFICE_AMENITY_READ`, que
   * quem cura trilha não tem - ver `backofficeApi.getDiscoveryFilters`.
   */
  const {
    data: vocabulary,
    loading: vocabularyLoading,
    error: vocabularyError,
  } = useAsync<MerchantDiscoveryOptionsDto>(() => backofficeApi.getDiscoveryFilters(), []);

  const cityOptions = useMemo(
    () => [
      { value: '', label: 'Todas as cidades' },
      ...(localityPage?.content ?? []).map((l) => ({ value: l.id, label: `${l.name}, ${l.state}` })),
    ],
    [localityPage]
  );

  const amenityLabels = useMemo(() => {
    const map: Record<string, string> = {};
    (vocabulary?.amenities ?? []).forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, [vocabulary]);

  const priceSymbols = useMemo(() => {
    const map: Record<number, string> = {};
    (vocabulary?.priceRanges ?? []).forEach((option) => {
      map[option.value] = option.symbol;
    });
    return map;
  }, [vocabulary]);

  const trails = data?.content ?? [];
  const totalItems = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  /* ---------------------------------------------------------------- prévia */

  const [audience, setAudience] = useState<{ count: number; signature: string } | null>(null);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [counting, setCounting] = useState<boolean>(false);

  const currentFilter = useMemo(() => buildTrailFilter(form), [form]);
  const currentSignature = filterSignature(currentFilter, form.localityId);
  const debouncedSignature = useDebouncedValue(currentSignature, 500);
  const filterIsEmpty = isFilterEmpty(currentFilter);

  /**
   * A contagem se refaz sozinha quando o filtro para de mudar.
   *
   * Automática, e não atrás de um botão, porque é o número que ensina o efeito
   * de cada caixa marcada - pedido sob demanda ele viraria uma etapa a pular. O
   * `signature` é o que impede a enxurrada: reordenar as mesmas comodidades ou
   * desmarcar e remarcar produz o mesmo texto e nenhuma requisição nova.
   */
  const ticket = useRef(0);
  useEffect(() => {
    if (!isOpen) return;
    // A emptiness é reavaliada aqui dentro, e não como dependência: fora, ela
    // muda no primeiro clique e dispararia a contagem com a assinatura antiga,
    // pedindo o mesmo número duas vezes e piscando "o filtro mudou" no meio.
    if (isFilterEmpty(currentFilter)) {
      setAudience(null);
      setAudienceError(null);
      setCounting(false);
      return;
    }
    const mine = (ticket.current += 1);
    setCounting(true);
    setAudienceError(null);
    backofficeApi
      .previewTrail({ filter: currentFilter, localityId: form.localityId || null })
      .then((result) => {
        if (mine !== ticket.current) return;
        setAudience({ count: result.count, signature: currentSignature });
      })
      .catch((err) => {
        if (mine !== ticket.current) return;
        setAudience(null);
        setAudienceError(
          extractErrorMessage(err, 'Não foi possível contar as lojas desta trilha.', TRAIL_ERROR_MESSAGES)
        );
      })
      .finally(() => {
        if (mine === ticket.current) setCounting(false);
      });
    // `currentFilter` e `form.localityId` são lidos pelo `signature`; incluí-los
    // dispararia a cada tecla e desfaria o debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSignature]);

  const audienceIsStale = audience !== null && audience.signature !== currentSignature;

  /* ------------------------------------------------------------ formulário */

  const resetToFirstPage = (): void => setPage(0);

  const openCreate = (): void => {
    setEditingId(null);
    setForm(emptyTrailForm());
    setAudience(null);
    setAudienceError(null);
    setIsOpen(true);
  };

  const openEdit = (trail: BackofficeTrailDto): void => {
    setEditingId(trail.id);
    setForm(toTrailForm(trail));
    setAudience(null);
    setAudienceError(null);
    setIsOpen(true);
  };

  const update = <K extends keyof TrailFormState>(key: K, value: TrailFormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleAmenity = (code: string): void => {
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(code)
        ? prev.amenities.filter((item) => item !== code)
        : [...prev.amenities, code],
    }));
  };

  const togglePriceRange = (value: number): void => {
    setForm((prev) => ({
      ...prev,
      priceRanges: prev.priceRanges.includes(value)
        ? prev.priceRanges.filter((item) => item !== value)
        : [...prev.priceRanges, value],
    }));
  };

  const submit = async (): Promise<void> => {
    const problem = validateTrail(form);
    if (problem) {
      showNotification(problem, 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = toTrailRequest(form);
      if (editingId) {
        await backofficeApi.updateTrail(editingId, payload);
        showNotification('Trilha atualizada com sucesso.', 'success');
      } else {
        await backofficeApi.createTrail(payload);
        showNotification('Trilha criada com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar a trilha.', TRAIL_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Ativar/desativar na própria linha.
   *
   * Reenvia o resto da trilha como está, e **o slug junto**: o `PUT` é total, e
   * o endereço só se move quando chega um slug diferente do atual - mandar o
   * mesmo é o que garante que um clique no interruptor não mexa numa URL
   * indexada.
   */
  const toggleActive = async (trail: BackofficeTrailDto): Promise<void> => {
    try {
      await backofficeApi.updateTrail(trail.id, {
        name: trail.name,
        filter: trail.filter,
        slug: trail.slug,
        tagline: trail.tagline,
        icon: trail.icon,
        localityId: trail.localityId,
        startsAt: trail.startsAt,
        endsAt: trail.endsAt,
        sortOrder: trail.sortOrder,
        active: !trail.active,
        seoTitle: trail.seo?.title ?? null,
        seoDescription: trail.seo?.description ?? null,
        seoKeywords: trail.seo?.keywords ?? null,
      });
      showNotification(trail.active ? 'Trilha desativada.' : 'Trilha ativada.', 'success');
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível alterar a situação.', TRAIL_ERROR_MESSAGES),
        'error'
      );
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteTrail(deleting.id);
      showNotification('Trilha excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível excluir a trilha.', TRAIL_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setRemoving(false);
    }
  };

  /* ---------------------------------------------------------------- prévia */

  const audienceContent = (): React.ReactNode => {
    if (filterIsEmpty) {
      return (
        <AudiencePanel $warn={false}>
          <Store size={16} />
          <span>
            Escolha ao menos um critério para saber quantas lojas a trilha pega. Sem nenhum, ela
            seria a vitrine inteira com outro nome - e o servidor recusa.
          </span>
        </AudiencePanel>
      );
    }
    if (audienceError) {
      return (
        <AudiencePanel $warn>
          <AlertTriangle size={16} />
          <span>{audienceError}</span>
        </AudiencePanel>
      );
    }
    if (counting || audience === null) {
      return (
        <AudiencePanel $warn={false}>
          <Spinner size={16} />
          <span>Contando as lojas deste filtro...</span>
        </AudiencePanel>
      );
    }
    const described = describeTrailAudience(audience.count);
    return (
      <AudiencePanel $warn={!described.publishable}>
        {described.publishable ? <Store size={16} /> : <AlertTriangle size={16} />}
        <span>
          {described.message}
          {audienceIsStale && ' (o filtro mudou - recontando)'}
        </span>
      </AudiencePanel>
    );
  };

  const slugHint = editingId
    ? 'Renomear a trilha NÃO muda o endereço: /explorar/pet-friendly está indexado, e trocá-lo transformaria cada link compartilhado em 404. Só mude este campo quando quiser mesmo trocar a URL.'
    : `Em branco, o endereço sai do nome: /explorar/${previewSlug(form.name) || 'trilha'}. Depois de criada, renomear a trilha não muda mais o endereço.`;

  return (
    <>
      <PageHeader
        title="Trilhas do /explorar"
        subtitle="Curadoria das trilhas de intenção. Sem cidade, a trilha vale para todas; sem vigência, vale sempre."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova trilha
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total (respeita o filtro)" value={formatNumber(totalItems)} />
        <StatCard label="No ar (nesta página)" value={formatNumber(trails.filter((t) => t.visible).length)} />
        <StatCard
          label="Ativas fora do ar (nesta página)"
          value={formatNumber(trails.filter((t) => t.active && !t.visible).length)}
        />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome ou endereço..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={ACTIVE_FILTER}
          value={activeFilter}
          onChange={(e) => {
            setActiveFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={cityOptions}
          value={cityFilter}
          onChange={(e) => {
            setCityFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={trails.length === 0}
        emptyTitle="Nenhuma trilha encontrada"
        emptyDescription="As trilhas são os atalhos de intenção do /explorar: 'pet friendly', 'para trabalhar', 'sem gastar muito'."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Trilha</th>
                <th>Critérios</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Onde aparece</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {trails.map((trail) => {
                const status = describeTrailVisibility(trail);
                return (
                  <tr key={trail.id}>
                    <td>
                      <TitleCell>
                        <Compass size={14} />
                        <div>
                          <strong>{trail.name}</strong>
                          <br />
                          <span>/explorar/{trail.slug}</span>
                        </div>
                      </TitleCell>
                    </td>
                    <td>
                      <CriteriaCell>
                        {criteriaChips(trail, amenityLabels, priceSymbols).map((chip) => (
                          <Badge key={chip} tone="info">
                            {chip}
                          </Badge>
                        ))}
                      </CriteriaCell>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{trail.localityLabel ?? 'Todas as cidades'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <StatusCell>
                        <Badge tone={status.tone}>{status.label}</Badge>
                        {status.reason && trail.active && <small>{status.reason}</small>}
                      </StatusCell>
                    </td>
                    <td>
                      <RowActions>
                        <Switch
                          checked={trail.active}
                          onChange={() => toggleActive(trail)}
                          aria-label={trail.active ? 'Desativar trilha' : 'Ativar trilha'}
                        />
                        <ActionButton aria-label="Editar trilha" title="Editar" onClick={() => openEdit(trail)}>
                          <Edit3 size={16} />
                        </ActionButton>
                        <ActionButton
                          aria-label="Excluir trilha"
                          title="Excluir"
                          color={theme.error}
                          onClick={() => setDeleting(trail)}
                        >
                          <Trash2 size={16} />
                        </ActionButton>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar trilha' : 'Nova trilha'}
        width="760px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome"
          required
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex.: Pet friendly"
        />
        <TextInput
          label="Endereço (slug)"
          value={form.slug}
          onChange={(e) => update('slug', e.target.value)}
          placeholder={editingId ? '' : 'Em branco, sai do nome'}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FieldHint>{slugHint}</FieldHint>

        <TextInput
          label="Chamada"
          value={form.tagline}
          onChange={(e) => update('tagline', e.target.value)}
          placeholder="Onde o seu cachorro entra junto"
          containerStyle={{ marginTop: '0.75rem' }}
        />

        <FieldRow>
          <Select
            label="Cidade"
            options={cityOptions}
            value={form.localityId}
            onChange={(e) => update('localityId', e.target.value)}
          />
          <Select
            label="Ícone"
            options={ICON_OPTIONS}
            value={form.icon}
            onChange={(e) => update('icon', e.target.value)}
          />
        </FieldRow>
        <FieldHint>
          Sem cidade, a trilha aparece em todas. A lista de ícones é fechada: é a que o site sabe
          desenhar - qualquer outro nome cairia na bússola genérica.
        </FieldHint>

        <Section>
          <SectionTitle>Critérios da trilha</SectionTitle>
          <FieldHint>
            As comodidades pedidas são exigidas <strong>todas juntas</strong>; entre as faixas de
            preço basta <strong>uma</strong>. O vocabulário é o mesmo dos filtros da vitrine, e é por
            isso que o botão "ver na vitrine" do site abre /empresas com estas caixas marcadas.
          </FieldHint>

          {vocabularyError ? (
            <AudiencePanel $warn>
              <AlertTriangle size={16} />
              <span>
                Não foi possível carregar o vocabulário de comodidades e faixas de preço. Sem ele o
                filtro não pode ser montado - atualize a página e tente de novo.
              </span>
            </AudiencePanel>
          ) : (
            <>
              <OptionGrid>
                {vocabularyLoading && <span style={{ fontSize: '0.8rem' }}>Carregando o vocabulário...</span>}
                {(vocabulary?.amenities ?? []).map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    // O `Checkbox` não amarra o rótulo visual ao input, então
                    // sem isto o leitor de tela anuncia uma caixa sem nome.
                    aria-label={option.label}
                    checked={form.amenities.includes(option.value)}
                    onChange={() => toggleAmenity(option.value)}
                  />
                ))}
              </OptionGrid>

              <OptionGrid>
                {(vocabulary?.priceRanges ?? []).map((option) => (
                  <Checkbox
                    key={option.value}
                    label={`${option.symbol} · ${option.label}`}
                    aria-label={`${option.symbol} ${option.label}`}
                    checked={form.priceRanges.includes(option.value)}
                    onChange={() => togglePriceRange(option.value)}
                  />
                ))}
              </OptionGrid>
            </>
          )}

          <FieldRow>
            <TextInput
              label="Nota mínima"
              type="number"
              min={0}
              max={5}
              step={0.1}
              value={form.minRating}
              onChange={(e) => update('minRating', e.target.value)}
              placeholder="Em branco = não filtra"
            />
            <TextInput
              label="Cashback mínimo (%)"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={form.minCashback}
              onChange={(e) => update('minCashback', e.target.value)}
              placeholder="Em branco = não filtra"
            />
          </FieldRow>

          <InlineToggle>
            <Switch
              checked={form.openNow}
              onChange={(value) => update('openNow', value)}
              aria-label="Só quem está aberto agora"
            />
            Só quem está aberto agora
          </InlineToggle>
          <FieldHint>
            Desligado significa "não me importo", e não "só os fechados" - é assim que o site lê o
            filtro, e o servidor grava o desligado como ausente.
          </FieldHint>

          {audienceContent()}
        </Section>

        <Section>
          <SectionTitle>Vigência e exibição</SectionTitle>
          <FieldRow>
            <TextInput
              label="Válida a partir de"
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => update('startsAt', e.target.value)}
            />
            <TextInput
              label="Válida até"
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => update('endsAt', e.target.value)}
            />
          </FieldRow>
          <FieldHint>
            Em branco dos dois lados significa "sempre". Uma trilha ativa fora da janela continua
            ativa e <strong>não aparece</strong> no site - é a diferença que a coluna Situação
            mostra.
          </FieldHint>

          <FieldRow>
            <TextInput
              label="Ordem"
              type="number"
              value={String(form.sortOrder)}
              onChange={(e) => update('sortOrder', Number(e.target.value) || 0)}
            />
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.4rem' }}>
              <Checkbox
                label="Trilha ativa"
                aria-label="Trilha ativa"
                checked={form.active}
                onChange={(e) => update('active', e.target.checked)}
              />
            </div>
          </FieldRow>
        </Section>

        <Section>
          <SectionTitle>SEO</SectionTitle>
          <TextInput
            label="Título de SEO"
            value={form.seoTitle}
            onChange={(e) => update('seoTitle', e.target.value)}
            placeholder="Lugares pet friendly na sua cidade | Kardume Urbano"
            containerStyle={{ marginTop: '0.6rem' }}
          />
          <TextInput
            label="Descrição de SEO"
            value={form.seoDescription}
            onChange={(e) => update('seoDescription', e.target.value)}
            placeholder="Cafés, restaurantes e lojas que aceitam pets..."
            containerStyle={{ marginTop: '0.75rem' }}
          />
          <TagsInput
            label="Palavras-chave"
            value={form.seoKeywords}
            onChange={(keywords) => update('seoKeywords', keywords)}
            placeholder="Digite e pressione Enter..."
            containerStyle={{ marginTop: '0.75rem' }}
          />
          <FieldHint>
            Em branco, o site cai para o texto da própria trilha - nunca para uma frase genérica.
          </FieldHint>
        </Section>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir trilha"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Excluir
            </Button>
          </>
        }
      >
        <ConfirmText>
          Tem certeza que deseja excluir <strong>{deleting?.name ?? ''}</strong>? O endereço{' '}
          <strong>/explorar/{deleting?.slug ?? ''}</strong> deixará de responder, e ele volta a ficar
          livre para uma trilha nova.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default TrailsPage;
